import React, { Suspense, lazy, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";

import ScrollToTop from "./components/ScrollToTop";
import { HelmetProvider } from "react-helmet-async";

// The www landing page is the one route that must paint without a second network
// round trip, so it is the only page bundled into the entry chunk. Everything else
// is split out and fetched on demand — a visitor to `/` no longer downloads the
// blog, every digital card and every QR generator before the hero can render.
import Websites from "./pages/Websites";

const Home = lazy(() => import("./pages/Home"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const ContactUs = lazy(() => import("./pages/ContactUs"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Discovery = lazy(() => import("./pages/Discovery"));
const BlogListing = lazy(() => import("./pages/BlogHome"));
const DynamicBlog = lazy(() => import("./pages/DynamicBlog"));
const MumbaiZingDemo = lazy(() => import("./pages/client-demos/mumbai-zing"));

const AppointmentConfirmation = lazy(() => import("./pages/AppointmentConfirmation"));
const Appointment = lazy(() => import("./pages/Appointment"));
const Network = lazy(() => import("./pages/Network"));
const NetworkQrGenerator = lazy(() => import("./pages/NetworkQrGenerator"));
const HoldingScreen = lazy(() => import("./pages/client-demos/HoldingScreen"));
const DigitalConnectPricing = lazy(() => import("./pages/dev/DigitalConnectPricing"));
const Trademark = lazy(() => import("./pages/Trademark"));
const Welcome = lazy(() => import("./pages/Welcome"));
const NewVenture = lazy(() => import("./pages/NewVenture"));
const ClientPage = lazy(() => import("./pages/clients/ClientPage"));

const Convu = lazy(() => import("./pages/Convu"));
const ConvuConfirmation = lazy(() => import("./pages/ConvuConfirmation"));
const FormHubSpot = lazy(() => import("./pages/FormHubSpot"));

// Arkana Rama
const MargaretDigitalCard = lazy(() => import("./pages/arkana-rama/margaret"));
const MargaretQrGenerator = lazy(() => import("./pages/arkana-rama/margaret-qr-generator"));

const MarcDigitalCard = lazy(() => import("./pages/arkana-rama/marc"));
const MarcQrGenerator = lazy(() => import("./pages/arkana-rama/marc-qr-generator"));

const ArunDigitalCard = lazy(() => import("./pages/arkana-rama/arun"));
const ArunQrGenerator = lazy(() => import("./pages/arkana-rama/arun-qr-generator"));

const MoDigitalCard = lazy(() => import("./pages/arkana-rama/mo"));
const MoQrGenerator = lazy(() => import("./pages/arkana-rama/mo-qr-generator"));

const IwanDigitalCard = lazy(() => import("./pages/arkana-rama/iwan"));
const IwanQrGenerator = lazy(() => import("./pages/arkana-rama/iwan-qr-generator"));

const MartinDigitalCard = lazy(() => import("./pages/arkana-rama/martin"));
const MartinQrGenerator = lazy(() => import("./pages/arkana-rama/martin-qr-generator"));

const AdamDigitalCard = lazy(() => import("./pages/arkana-rama/adam"));
const AdamQrGenerator = lazy(() => import("./pages/arkana-rama/adam-qr-generator"));

const DaraDigitalCard = lazy(() => import("./pages/arkana-rama/dara"));
const DaraQrGenerator = lazy(() => import("./pages/arkana-rama/dara-qr-generator"));

const DavidDigitalCard = lazy(() => import("./pages/arkana-rama/david"));
const DavidQrGenerator = lazy(() => import("./pages/arkana-rama/david-qr-generator"));

const EvgeniDigitalCard = lazy(() => import("./pages/arkana-rama/evgeni"));
const EvgeniQrGenerator = lazy(() => import("./pages/arkana-rama/evgeni-qr-generator"));

const MatthewDigitalCard = lazy(() => import("./pages/arkana-rama/matthew"));
const MatthewQrGenerator = lazy(() => import("./pages/arkana-rama/matthew-qr-generator"));

const NathanaelDigitalCard = lazy(() => import("./pages/arkana-rama/nathanael"));
const NathanaelQrGenerator = lazy(() => import("./pages/arkana-rama/nathanael-qr-generator"));

const ReshmaDigitalCard = lazy(() => import("./pages/arkana-rama/reshma"));
const ReshmaQrGenerator = lazy(() => import("./pages/arkana-rama/reshma-qr-generator"));

const JoanneDigitalCard = lazy(() => import("./pages/arkana-rama/joanne"));
const JoanneQrGenerator = lazy(() => import("./pages/arkana-rama/joanne-qr-generator"));

const StevenDigitalCard = lazy(() => import("./pages/arkana-rama/steven"));
const StevenQrGenerator = lazy(() => import("./pages/arkana-rama/steven-qr-generator"));

const MartinLeeDigitalCard = lazy(() => import("./pages/arkana-rama/martin-lee"));
const MartinLeeQrGenerator = lazy(() => import("./pages/arkana-rama/martin-lee-qr-generator"));

const ArkanaRamaDirectory = lazy(() => import("./pages/arkana-rama/index"));

// Aria Grace
const EzanaDigitalCard = lazy(() => import("./pages/aria-grace/ezana"));
const EzanaQrGenerator = lazy(() => import("./pages/aria-grace/ezana-qr-generator"));

const LindsayDigitalCard = lazy(() => import("./pages/aria-grace/lindsay"));
const LindsayQrGenerator = lazy(() => import("./pages/aria-grace/lindsay-qr-generator"));

const EmaDigitalCard = lazy(() => import("./pages/aria-grace/ema"));
const EmaQrGenerator = lazy(() => import("./pages/aria-grace/ema-qr-generator"));

// Avanti
const DragonsDenWorkshop = lazy(() => import("./pages/avanti/dragons-den-workshop"));

// Warwick Congress
const WarwickStartupLaunch = lazy(() => import("./pages/warwick-congress/startup-launch"));

// Resources
const ArkanaRamaCall = lazy(() => import("./pages/resources/arkana-rama-call"));

/**
 * Route-transition placeholder. Deliberately just the page background: a spinner
 * that flashes for 80ms reads as jank, and any content here would shift the layout
 * the moment the real page mounts.
 */
function RouteFallback() {
  return <div className="min-h-screen bg-[#060B14]" aria-busy="true" />;
}

function App() {
  // Analytics is not part of the first paint. Loading Clarity during idle keeps its
  // script off the critical path on mobile, where main-thread time is the constraint.
  useEffect(() => {
    let cancelled = false;
    const start = () => {
      if (cancelled) return;
      import("@microsoft/clarity")
        .then(({ default: Clarity }) => Clarity.init("tm60vzav8p"))
        .catch(() => {});
    };

    const id =
      typeof window.requestIdleCallback === "function"
        ? window.requestIdleCallback(start, { timeout: 4000 })
        : window.setTimeout(start, 2500);

    return () => {
      cancelled = true;
      if (typeof window.cancelIdleCallback === "function") window.cancelIdleCallback(id);
      else window.clearTimeout(id);
    };
  }, []);

  // The main domain now leads with the website-building service; the DataPulse AI
  // project listing moved to apps.mydatapulse.co.uk. Local dev and any other host
  // get the website-building site so the primary page is what you see by default.
  const isAppsSubdomain = window.location.hostname === "apps.mydatapulse.co.uk";

  return (
    <Router>
      <HelmetProvider>
        <ScrollToTop />
        <div className="App">
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route path="/" element={isAppsSubdomain ? <Home /> : <Websites />} />

              <Route path="/contact-us" element={<ContactUs />} />
              <Route path="/discovery" element={<Discovery />} />
              <Route path="/appointment-confirmation" element={<AppointmentConfirmation />} />
              <Route path="/appointment" element={<Appointment />} />
              <Route path="/contact" element={<Network />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/trademark" element={<Trademark />} />

              {/* Retired DataPulse AI consultancy pages. These live in Google's index, so they
                  must resolve rather than 404. Handled here so the behaviour is host-agnostic
                  and survives a hosting change; the real 301s belong in the host's redirect
                  rules (see deploy/amplify-redirects.json) because only those pass link equity. */}
              <Route path="/get-in-touch" element={<Navigate to="/" replace />} />
              <Route path="/journey" element={<Navigate to="/" replace />} />

              {/* Direct aliases: both sites stay reachable on every host, including localhost. */}
              <Route path="/websites" element={<Websites />} />
              <Route path="/apps" element={<Home />} />
              <Route path="/welcome" element={<Welcome />} />
              <Route path="/congrats" element={<NewVenture />} />
              <Route path="/clients/:slug" element={<ClientPage />} />

              <Route path="/convu" element={<Convu />} />
              <Route path="/convu-confirmation" element={<ConvuConfirmation />} />

              <Route path="/form-hubspot" element={<FormHubSpot />} />

              <Route path="/network" element={<Network />} />
              <Route path="/network-qr-generator" element={<NetworkQrGenerator />} />

              {/* <Route path="/demo-payment-confirmation" element={<DemoPaymentConfirmation />} /> */}
              {/* <Route path="/chat" element={<Chat />} /> */}

              {/* QR Codes */}
              <Route path="/qr/hoodie" element={<Navigate to="/contact-us" replace />} />
              {/* <Route path="/whatsapp-assistant" element={<Navigate to="https://api.whatsapp.com/send/?phone=447429450382&text=Hi%20DataPulse!%20%F0%9F%91%8B%F0%9F%8F%BD%20I%27m%20interested%20in%20how%20you%20can%20help%20grow%20my%20business%20%F0%9F%9A%80" replace />}/> */}

              {/* Client Demos */}
              <Route path="/demo/development" element={<HoldingScreen />} />
              <Route path="/demo/mumbai-zing" element={<MumbaiZingDemo />} />

              {/* Blog */}
              <Route path="/blog" element={<BlogListing />} />
              <Route path="/blog/:slug" element={<DynamicBlog />} />

              {/* Dev */}
              {/* <Route path="/dev/login" element={<Login />} />
              <Route path="/dev/user-dashboard" element={<UserDashboard />} /> */}
              <Route path="/dev/the-digital-connect" element={<DigitalConnectPricing />} />

              {/* Arkana Rama */}
              <Route path="/arkana-rama" element={<ArkanaRamaDirectory />} />
              <Route path="/arkana-rama/margaret" element={<MargaretDigitalCard />} />
              <Route path="/arkana-rama/margaret-qr-generator" element={<MargaretQrGenerator />} />

              <Route path="/arkana-rama/marc" element={<MarcDigitalCard />} />
              <Route path="/arkana-rama/marc-qr-generator" element={<MarcQrGenerator />} />

              <Route path="/arkana-rama/arun" element={<ArunDigitalCard />} />
              <Route path="/arkana-rama/arun-qr-generator" element={<ArunQrGenerator />} />

              <Route path="/arkana-rama/mo" element={<MoDigitalCard />} />
              <Route path="/arkana-rama/mo-qr-generator" element={<MoQrGenerator />} />

              <Route path="/arkana-rama/iwan" element={<IwanDigitalCard />} />
              <Route path="/arkana-rama/iwan-qr-generator" element={<IwanQrGenerator />} />

              <Route path="/arkana-rama/martin" element={<MartinDigitalCard />} />
              <Route path="/arkana-rama/martin-qr-generator" element={<MartinQrGenerator />} />

              <Route path="/arkana-rama/adam" element={<AdamDigitalCard />} />
              <Route path="/arkana-rama/adam-qr-generator" element={<AdamQrGenerator />} />

              <Route path="/arkana-rama/dara" element={<DaraDigitalCard />} />
              <Route path="/arkana-rama/dara-qr-generator" element={<DaraQrGenerator />} />

              <Route path="/arkana-rama/david" element={<DavidDigitalCard />} />
              <Route path="/arkana-rama/david-qr-generator" element={<DavidQrGenerator />} />

              <Route path="/arkana-rama/evgeni" element={<EvgeniDigitalCard />} />
              <Route path="/arkana-rama/evgeni-qr-generator" element={<EvgeniQrGenerator />} />

              <Route path="/arkana-rama/matthew" element={<MatthewDigitalCard />} />
              <Route path="/arkana-rama/matthew-qr-generator" element={<MatthewQrGenerator />} />

              <Route path="/arkana-rama/nathanael" element={<NathanaelDigitalCard />} />
              <Route path="/arkana-rama/nathanael-qr-generator" element={<NathanaelQrGenerator />} />

              <Route path="/arkana-rama/reshma" element={<ReshmaDigitalCard />} />
              <Route path="/arkana-rama/reshma-qr-generator" element={<ReshmaQrGenerator />} />

              <Route path="/arkana-rama/joanne" element={<JoanneDigitalCard />} />
              <Route path="/arkana-rama/joanne-qr-generator" element={<JoanneQrGenerator />} />

              <Route path="/arkana-rama/steven" element={<StevenDigitalCard />} />
              <Route path="/arkana-rama/steven-qr-generator" element={<StevenQrGenerator />} />

              <Route path="/arkana-rama/martin-lee" element={<MartinLeeDigitalCard />} />
              <Route path="/arkana-rama/martin-lee-qr-generator" element={<MartinLeeQrGenerator />} />

              {/* Aria Grace */}
              <Route path="/aria-grace/ezana" element={<EzanaDigitalCard />} />
              <Route path="/aria-grace/ezana-qr-generator" element={<EzanaQrGenerator />} />

              <Route path="/aria-grace/lindsay" element={<LindsayDigitalCard />} />
              <Route path="/aria-grace/lindsay-qr-generator" element={<LindsayQrGenerator />} />

              <Route path="/aria-grace/ema" element={<EmaDigitalCard />} />
              <Route path="/aria-grace/ema-qr-generator" element={<EmaQrGenerator />} />

              {/* Avanti */}
              <Route path="/avanti/dragons-den-workshop" element={<DragonsDenWorkshop />} />

              {/* Warwick Congress */}
              <Route path="/warwick-congress/startup-launch" element={<WarwickStartupLaunch />} />

              {/* Resources */}
              <Route path="/meetings/arkana-rama-call" element={<ArkanaRamaCall />} />

              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </div>
      </HelmetProvider>
    </Router>
  );
}

export default App;
